import { createReducer } from '@reduxjs/toolkit';
import { sectionTypes } from '@utils/sectionEnums.js';

import * as menuActions from './menuActions.js';

const initialState = {
  requesting: false,
  error: null,

  id: 0,
  uid: '',
  sid: 0,
  type: sectionTypes.menu?.type ?? 'menu',
  isActive: false,
  isAdded: false,

  // ===== CONTENT =====
  heading: {
    id: 0,
    blockSid: 0,
    title: '',
    titleColor: '#000000',
    description: '',
    descriptionColor: '#333333',
    image: null,
    textAlign: 'center',
    divider: null,
    position: 'top',
    layout: null,
  },

  menuType: null,
  pdfUrl: null,
  pdfFiles: [],
  view: null,
  layout: null,
  categories: null,
  items: [],

  // Cart & checkout (MenuBlock.IsCartEnabled / .AcceptsOnlinePayment)
  isCartEnabled: false,
  acceptsOnlinePayment: false,

  // ===== DESIGN =====
  containerDesign: {
    isBackground: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    border: 'soft',
    shadow: null,
    padding: 12,
    margin: 15,
  },

  itemDesign: {
    backgroundColor: '#F5F5F5',
    textColor: '#000000',
    border: 'soft',
    shadow: 'none',
  },

  buttonDesign: {
    backgroundColor: null,
    textColor: null,
    opacity: null,
    border: null,
    shadow: null,
    shape: null,
    text: null,
    height: null,
  },

  designView: 'grid',
  designLayout: 'cards',
  itemTextAlign: null,
  itemTitleSize: null,
  itemLayout: null,
  itemFields: null,
  itemColumns: null,
};

const menu = createReducer(initialState, (builder) => {
  builder
    .addCase(menuActions.menuUpdateId, (state, { payload: id }) => ({ ...state, id }))
    .addCase(menuActions.menuUpdateUID, (state, { payload: uid }) => ({ ...state, uid }))
    .addCase(menuActions.menuUpdateSID, (state, { payload: sid }) => ({ ...state, sid }))
    .addCase(menuActions.menuUpdateIsAdded, (state, { payload: isAdded }) => ({ ...state, isAdded }))
    .addCase(menuActions.menuUpdateIsActive, (state, { payload: isActive }) => ({ ...state, isActive }))

    .addCase(menuActions.menuStore, (state, { payload }) => {
      const { id, uid, sid, isActive, isAdded, data } = payload ?? {};

      const blockContent = data?.blockContent ?? {};
      const blockDesign = data?.blockDesign ?? {};

      const heading = blockContent?.heading ?? {};
      const items = blockContent?.items ?? [];
      const categories = blockContent?.categories ?? null;

      const containerDesign = blockDesign?.containerDesign ?? {};
      const itemDesign = blockDesign?.itemDesign ?? {};
      const buttonDesign = blockDesign?.buttonDesign ?? {};

      return {
        ...state,
        requesting: false,
        error: null,

        id: id ?? 0,
        uid: uid ?? '',
        sid: sid ?? 0,
        isActive: Boolean(isActive),
        isAdded: Boolean(isAdded),

        heading: {
          ...state.heading,
          ...heading,
          title: heading?.title ?? state.heading.title,
          description: heading?.description ?? state.heading.description,
          textAlign: heading?.textAlign ?? state.heading.textAlign,
        },

        menuType: blockContent?.menuType ?? null,
        pdfUrl: blockContent?.pdfUrl ?? null,
        pdfFiles: blockContent?.pdfFiles ?? [],
        view: blockContent?.view ?? null,
        layout: blockContent?.layout ?? null,
        categories,
        items,

        isCartEnabled: Boolean(blockContent?.isCartEnabled),
        acceptsOnlinePayment: Boolean(blockContent?.acceptsOnlinePayment),

        containerDesign: { ...state.containerDesign, ...containerDesign },
        itemDesign: {
          ...state.itemDesign,
          ...itemDesign,
          itemLayout: blockDesign?.itemLayout ?? itemDesign?.layout ?? itemDesign?.itemLayout ?? state.itemDesign.itemLayout,
        },
        buttonDesign: { ...state.buttonDesign, ...buttonDesign },

        designView: blockDesign?.view ?? state.designView,
        designLayout: blockDesign?.layout ?? state.designLayout,
        itemTextAlign: blockDesign?.itemTextAlign ?? state.itemTextAlign,
        itemTitleSize: blockDesign?.itemTitleSize ?? state.itemTitleSize,
        itemLayout: blockDesign?.itemLayout ?? itemDesign?.layout ?? state.itemLayout,
        itemFields: blockDesign?.itemFields ?? state.itemFields,
        itemColumns: blockDesign?.itemColumns ?? state.itemColumns,
      };
    })

    .addCase(menuActions.menuClear, () => ({ ...initialState }))

    .addCase(menuActions.menuUpdateRequest, (state) => ({ ...state, requesting: true, error: null }))
    .addCase(menuActions.menuUpdateIsLoading, (state, { payload }) => {
      state.requesting = payload;
    })
    .addCase(menuActions.menuUpdateError, (state, { payload }) => ({ ...state, error: payload, requesting: false }))
    .addCase(menuActions.menuUpdateStore, (state) => ({ ...state, error: null, requesting: false }))

    .addCase(menuActions.menuUpdateItems, (state, { payload: items }) => ({ ...state, items }))
    .addCase(menuActions.menuUpdatePdfFiles, (state, { payload: pdfFiles }) => ({ ...state, pdfFiles }))
    .addCase(menuActions.menuUpdateHeading, (state, { payload: heading }) => ({ ...state, heading: { ...state.heading, ...heading } }))
    .addCase(menuActions.menuUpdateBlockContent, (state, { payload }) => ({
      ...state,
      ...payload,
    }))
    .addCase(menuActions.menuUpdateBlockDesign, (state, { payload }) => {
      const { containerDesign, itemDesign, buttonDesign, view, layout, itemTextAlign, itemTitleSize, itemLayout, itemFields, itemColumns } = payload ?? {};

      if (containerDesign) {
        state.containerDesign = {
          ...state.containerDesign,
          ...containerDesign,
        };
      }

      if (itemDesign) {
        state.itemDesign = {
          ...state.itemDesign,
          ...itemDesign,
        };
      }

      if (buttonDesign) {
        state.buttonDesign = {
          ...state.buttonDesign,
          ...buttonDesign,
        };
      }

      if (view !== undefined) state.designView = view;
      if (layout !== undefined) state.designLayout = layout;
      if (itemTextAlign !== undefined) state.itemTextAlign = itemTextAlign;
      if (itemTitleSize !== undefined) state.itemTitleSize = itemTitleSize;
      if (itemLayout !== undefined) state.itemLayout = itemLayout;
      if (itemFields !== undefined) state.itemFields = itemFields;
      if (itemColumns !== undefined) state.itemColumns = itemColumns;
    });
});

export default menu;
